import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import {
  AddBusiness as AddBusinessIcon,
  ArrowForward as ArrowForwardIcon,
  ContentCopy as ContentCopyIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PartnerLayout from '../../components/partner/PartnerLayout';
import PartnerHelpPanel from '../../components/partner/PartnerHelpPanel';
import LoadingButton from '../../components/LoadingButton';
import partnerApiService from '../../services/partnerApi';
import { PartnerClientCreate, PartnerClientSummary } from '../../types/partner';
import { AccountUserAuthMethod } from '../../types/clientAccount';
import AuthMethodSelect from '../../components/AuthMethodSelect';
import { formatCredits } from '../../utils/credits';

// Clientes del PORTAL PARTNER: lista en tarjetas grandes + alta de cliente con
// la contraseña generada «de un solo vistazo» (se enseña UNA vez, con copiar).
// Con «Solo Google» no hay contraseña: el aviso explica cómo entrará el cliente.

const emptyForm = {
  name: '',
  company: '',
  owner_email: '',
  owner_full_name: '',
  max_employees: '1',
  // Mismas tres opciones que el alta del admin: el partner decide igual.
  auth_method: 'password' as AccountUserAuthMethod
};

const PartnerClientsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState<PartnerClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [form, setForm] = useState({ ...emptyForm });

  // Contraseña generada — un solo vistazo (no se puede volver a consultar).
  const [createdInfo, setCreatedInfo] = useState<{
    clientId: number | null;
    clientName: string;
    ownerEmail: string;
    password: string;
    // El backend intenta enviarla también al buzón del propietario; si no salió
    // (sin SMTP o fallo), se mantiene el texto de siempre: compártela tú.
    emailSent: boolean;
  } | null>(null);
  // Alta SOLO-GOOGLE: sin contraseña que compartir, pero el partner necesita
  // saber que su cliente ya puede entrar y por dónde.
  const [googleNotice, setGoogleNotice] = useState<string | null>(null);

  const loadClients = useCallback(async () => {
    try {
      setError('');
      const data = await partnerApiService.getClients();
      setClients(data);
    } catch (err: any) {
      console.error('Failed to load partner clients:', err);
      setError(err?.detail || t('partner.clients.loadError', 'No se pudieron cargar tus clientes'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // ?new=1 (CTA del onboarding del dashboard) abre el alta directamente.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setCreateOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Estado de la cuenta traducido; un estado nuevo del backend se enseña tal
  // cual en vez de quedarse en blanco.
  const statusLabel = (status: string) =>
    status === 'active'
      ? t('partner.clients.statusActive', 'Activa')
      : status === 'suspended'
        ? t('partner.clients.statusSuspended', 'Suspendida')
        : status;

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const maxEmployeesValid = /^\d+$/.test(form.max_employees.trim()) && Number(form.max_employees) >= 1;
  const canCreate =
    form.name.trim() !== '' &&
    form.owner_full_name.trim() !== '' &&
    isValidEmail(form.owner_email) &&
    maxEmployeesValid;

  const handleCreate = async () => {
    if (!canCreate) return;
    try {
      setCreateLoading(true);
      setCreateError('');
      const payload: PartnerClientCreate = {
        name: form.name.trim(),
        owner_email: form.owner_email.trim(),
        owner_full_name: form.owner_full_name.trim(),
        max_employees: Number(form.max_employees),
        auth_method: form.auth_method
      };
      if (form.company.trim()) payload.company = form.company.trim();
      const response = await partnerApiService.createClient(payload);
      toast.success(t('partner.clients.created', 'Cliente creado'));
      setCreateOpen(false);
      const ownerEmail = payload.owner_email;
      setForm({ ...emptyForm });
      if (response.generated_password) {
        setCreatedInfo({
          clientId: response.account?.id ?? null,
          clientName: response.account?.name || payload.name,
          ownerEmail,
          password: response.generated_password,
          emailSent: response.email_sent === true
        });
      } else if ((response.auth_methods || []).includes('google')) {
        setGoogleNotice(
          response.email_sent
            ? t(
                'partner.clients.googleOnlyCreatedEmailed',
                '{{email}} entrará con su cuenta de Google. Le hemos enviado un correo de bienvenida: no hay contraseña que compartir.',
                { email: ownerEmail }
              )
            : t(
                'partner.clients.googleOnlyCreated',
                '{{email}} entrará con su cuenta de Google: no hay contraseña que compartir.',
                { email: ownerEmail }
              )
        );
      }
      loadClients();
    } catch (err: any) {
      console.error('Failed to create partner client:', err);
      // 409 = cupo de clientes superado u otro límite: el detail del backend manda.
      setCreateError(err?.detail || t('partner.clients.createError', 'No se pudo crear el cliente'));
    } finally {
      setCreateLoading(false);
    }
  };

  const copyPassword = () => {
    if (createdInfo) {
      navigator.clipboard.writeText(createdInfo.password);
      toast.info(t('partner.clients.passwordCopied', 'Contraseña copiada'));
    }
  };

  return (
    <PartnerLayout>
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <PeopleIcon color="primary" fontSize="large" />
            <Typography variant="h4">{t('partner.clients.title', 'Clientes')}</Typography>
            {/* Ayuda de ESTA pantalla (qué es el cupo, qué saldo se ve aquí…). */}
            <PartnerHelpPanel screen="clients" variant="icon" />
          </Box>
          <Button variant="contained" size="large" startIcon={<AddBusinessIcon />} onClick={() => setCreateOpen(true)}>
            {t('partner.clients.create', 'Crear cliente')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : clients.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8, px: 3 }}>
            <PeopleIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('partner.clients.emptyTitle', 'Todavía no tienes clientes')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t('partner.clients.emptyText', 'Crea tu primer cliente y asígnale un empleado IA del catálogo.')}
            </Typography>
            <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={() => setCreateOpen(true)}>
              {t('partner.clients.create', 'Crear cliente')}
            </Button>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {clients.map((client) => (
              <Grid item xs={12} sm={6} md={4} key={client.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6">{client.name}</Typography>
                      <Chip
                        label={statusLabel(client.status)}
                        color={client.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    {client.company && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {client.company}
                      </Typography>
                    )}
                    <Box display="flex" gap={3} mt={1.5}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('partner.clients.employees', 'Empleados')}
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {client.employees_count ?? 0}
                          {typeof client.max_employees === 'number' ? ` / ${client.max_employees}` : ''}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('partner.clients.balance', 'Saldo')}
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {formatCredits(client.balance)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <Box px={2} pb={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate(`/partner/clients/${client.id}`)}
                    >
                      {t('partner.clients.viewDetail', 'Ver detalle')}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Diálogo alta de cliente */}
      <Dialog open={createOpen} onClose={() => !createLoading && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('partner.clients.create', 'Crear cliente')}</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError('')}>
              {createError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t('partner.clients.fields.name', 'Nombre de la cuenta') + ' *'}
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partner.clients.fields.company', 'Empresa') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partner.clients.fields.ownerEmail', 'Email del propietario') + ' *'}
            type="email"
            fullWidth
            value={form.owner_email}
            onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
            error={form.owner_email !== '' && !isValidEmail(form.owner_email)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partner.clients.fields.ownerName', 'Nombre del propietario') + ' *'}
            fullWidth
            value={form.owner_full_name}
            onChange={(e) => setForm({ ...form, owner_full_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partner.clients.fields.maxEmployees', 'Máx. empleados IA') + ' *'}
            type="number"
            fullWidth
            value={form.max_employees}
            onChange={(e) => setForm({ ...form, max_employees: e.target.value })}
            inputProps={{ min: 1, step: 1 }}
            error={!maxEmployeesValid}
            helperText={!maxEmployeesValid
              ? t('partner.clients.maxEmployeesInvalid', 'Debe ser un entero mayor o igual que 1')
              : t('partner.clients.maxEmployeesHelp', 'Cuántos empleados IA podrá contratar esta cuenta')}
            sx={{ mb: 2 }}
          />
          <AuthMethodSelect
            value={form.auth_method}
            onChange={(value) => setForm({ ...form, auth_method: value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton onClick={handleCreate} loading={createLoading} disabled={!canCreate} variant="contained">
            {t('common.create', 'Crear')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Alta SOLO-GOOGLE: no hay contraseña, pero sí algo que contar */}
      <Dialog open={googleNotice !== null} maxWidth="sm" fullWidth>
        <DialogTitle>{t('partner.clients.googleTitle', 'Cliente creado')}</DialogTitle>
        <DialogContent>
          <Alert severity="info">{googleNotice}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoogleNotice(null)} variant="contained">
            {t('common.close', 'Cerrar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contraseña generada — UN solo vistazo */}
      <Dialog open={createdInfo !== null} maxWidth="sm" fullWidth>
        <DialogTitle>{t('partner.clients.passwordTitle', 'Cliente creado: guarda su contraseña')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>{t('partner.clients.passwordOnce', 'Esta contraseña no volverá a mostrarse')}</AlertTitle>
            {t('partner.clients.passwordHint', 'Compártela con tu cliente por un canal seguro. Podrá cambiarla desde su portal.')}
            {/* Solo si el correo salió de verdad (envío fail-soft en backend). */}
            {createdInfo?.emailSent && (
              <Box sx={{ mt: 1 }}>
                {t('partner.clients.passwordAlsoEmailed', 'También se ha enviado por correo a {{email}}', {
                  email: createdInfo.ownerEmail
                })}
              </Box>
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('partner.clients.fields.account', 'Cuenta')}: <strong>{createdInfo?.clientName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('partner.clients.fields.ownerEmail', 'Email del propietario')}: <strong>{createdInfo?.ownerEmail}</strong>
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
          >
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.2rem', flexGrow: 1 }}>
              {createdInfo?.password}
            </Typography>
            <IconButton onClick={copyPassword} title={t('partner.clients.copyPassword', 'Copiar contraseña')}>
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          {createdInfo?.clientId !== null && createdInfo?.clientId !== undefined && (
            <Button onClick={() => navigate(`/partner/clients/${createdInfo.clientId}`)}>
              {t('partner.clients.goDetail', 'Ir al detalle')}
            </Button>
          )}
          <Button variant="contained" onClick={() => setCreatedInfo(null)}>
            {t('partner.clients.passwordSaved', 'Ya la he guardado')}
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerLayout>
  );
};

export default PartnerClientsPage;
